/**
 * The selected definition (the project page's right pane), in two tabs
 * kept in `?tab=definition|runs` (default `definition`), so a link opens
 * the same view:
 *
 * - Definition: the change lifecycle and its checks, the effective YAML
 *   (the default branch's for an enabled definition, with a banner and its
 *   diff on demand when an open MR changes it; the MR's diff for a proposed
 *   one), and the actions.
 * - Runs: the definition's runs (`DefinitionRuns`), mounted only while
 *   shown, so only then does it poll.
 *
 * The MR a Withdraw here closed is kept here, not in the Definition tab,
 * so Withdraw stays hidden for it across a tab switch while the detail
 * still names it (a slow or failed refetch).
 *
 * Read live from GitLab like the panel: no timer (ruling 9), and no focus
 * refetch of its own (the panel's shows whether this entry changed).
 */

import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DefinitionEditStart, WithdrawOutcome } from './DefinitionActions';
import { DefinitionHeader, DefinitionTab } from './DefinitionTab';
import { DefinitionTabs, type DefinitionTabId as Tab } from './DefinitionTabs';
import { DefinitionRuns } from './DefinitionRuns';
import { useDefinition } from '../hooks/useProjectPanel';
import { openUrl } from '../utils/definitionUrl';
import { isNotFoundError, panelErrorMessage } from '../utils/errors';
import type { DefinitionSummary } from '../types/projectPanel.types';

interface DefinitionDetailProps {
  projectId: string;
  /** The summary's `file_name` (`?def=`). */
  fileName: string;
  /** The list's entry for it: when a refetched list changes it, the detail refetches too. */
  summary: DefinitionSummary;
  /** The panel's `web_url` and `default_branch`, for Open in GitLab. */
  projectWebUrl: string | null;
  defaultBranch: string | null;
  /** Focus the heading once it shows: the viewer just selected this definition. */
  focusOnShow?: boolean;
  /** Opens the editor; Edit is offered only once the page passes it. */
  onEdit?: (start: DefinitionEditStart) => void;
  /** Why Edit is unavailable (the project's setup is incomplete); null when it isn't. */
  editBlocked?: string | null;
  /** The withdraw outcome (null clears it), kept by the page: a withdrawn proposed definition leaves the list. */
  onWithdrawOutcome: (outcome: WithdrawOutcome | null) => void;
}

export function DefinitionDetail(props: DefinitionDetailProps): ReactElement {
  const { projectId, fileName } = props;
  const { data: detail, error, isLoading, isFetching, refetch } = useDefinition(projectId, fileName);
  // The detail has no focus refetch of its own (the same GitLab scan as the
  // list): it refetches once each time the list shows this entry changed.
  const listed = JSON.stringify(props.summary);
  const synced = useRef(listed);
  useEffect(() => {
    if (listed === synced.current) return;
    synced.current = listed;
    if (detail !== undefined && JSON.stringify(detail.summary) !== listed) void refetch();
  }, [listed, detail, refetch]);
  const [params, setParams] = useSearchParams();
  // The MR a Withdraw here closed; the detail may still name it until it refetches.
  const [closedIid, setClosedIid] = useState<number | null>(null);
  const tab: Tab = params.get('tab') === 'runs' ? 'runs' : 'definition';
  const setTab = (next: Tab): void => {
    setParams((prev) => {
      const updated = new URLSearchParams(prev);
      updated.set('tab', next);
      return updated;
    });
  };

  if (isLoading) return <div className="loading-message">Loading the definition…</div>;
  if (detail === undefined || isNotFoundError(error)) {
    if (isNotFoundError(error)) {
      return <p className="project-page__hint">No definition {fileName} in this project.</p>;
    }
    return <div className="error-message" role="alert">{panelErrorMessage(error)}</div>;
  }
  return (
    <div className="def-detail">
      <DefinitionHeader detail={detail} focusOnShow={props.focusOnShow === true} />
      {error !== null && (
        <div className="error-message" role="alert">Couldn&apos;t refresh: {panelErrorMessage(error)}</div>
      )}
      <DefinitionTabs tab={tab} proposed={detail.state === 'proposed'} onChange={setTab} />
      <div className="def-detail__panel" role="tabpanel" id="def-tabpanel" aria-labelledby={`def-tab-${tab}`}>
        {tab === 'definition' ? (
          <DefinitionTab
            detail={detail}
            projectId={projectId}
            open={openUrl(detail, props.projectWebUrl, props.defaultBranch)}
            refreshing={isFetching}
            closedIid={closedIid}
            onClosed={setClosedIid}
            onWithdrawOutcome={props.onWithdrawOutcome}
            editBlocked={props.editBlocked ?? null}
            {...(props.onEdit !== undefined ? { onEdit: props.onEdit } : {})}
          />
        ) : (
          <DefinitionRuns
            projectId={projectId}
            name={detail.summary.name}
            state={detail.state}
            pendingMr={detail.summary.pending_mr}
          />
        )}
      </div>
    </div>
  );
}
